/**
 * CreateClientForm Component
 * Feature: 018-edit-client-association - User Story 2
 * 
 * Inline form for creating new clients from lead/ticket views
 * Includes duplicate detection with auto-link notification
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormInput } from '../shared/FormInput';

// Simplified schema for quick client creation
const createClientSchema = z.object({
  companyName: z.string().min(1, 'Company name is required').max(255),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Invalid email address').max(255),
  phone: z.string()
    .min(1, 'Phone is required')
    .max(20)
    .regex(/^\+?[1-9]\d{1,14}$/, 'Phone must be in international format (e.g., +12345678901)'),
  country: z.string().min(1, 'Country is required').max(100),
  city: z.string().max(100).optional().or(z.literal('')),
  street: z.string().max(255).optional().or(z.literal('')),
  clientSegment: z.enum(['BeautyProClinics', 'DropshipperReseller', 'Corporate', 'BrandOwner', 'InfluencerCreator', 'Other']).optional(), // Feature 019: Client segmentation
});

export type CreateClientFormData = z.infer<typeof createClientSchema>;

export interface CreateClientFormProps {
  onSubmit: (data: CreateClientFormData) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  duplicateWarning?: string | null;
}

export default function CreateClientForm({
  onSubmit,
  onCancel,
  isLoading = false,
  duplicateWarning = null,
}: CreateClientFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateClientFormData>({
    resolver: zodResolver(createClientSchema),
    defaultValues: {
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: 'USA',
      city: '',
      street: '',
      clientSegment: undefined,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Create New Account</h3>

      {/* Duplicate Warning */}
      {duplicateWarning && (
        <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">{duplicateWarning}</p>
            </div>
          </div>
        </div>
      )}

      {/* Company Name - Required */}
      <FormInput
        label="Company Name"
        required
        error={errors.companyName?.message}
        {...register('companyName')}
      />

      {/* First Name - Required */}
      <FormInput
        label="First Name"
        required
        placeholder="Primary contact's first name"
        error={errors.firstName?.message}
        {...register('firstName')}
      />

      {/* Last Name - Required */}
      <FormInput
        label="Last Name"
        required
        placeholder="Primary contact's last name"
        error={errors.lastName?.message}
        {...register('lastName')}
      />

      {/* Email - Required */}
      <FormInput
        label="Email"
        type="email"
        required
        error={errors.email?.message}
        {...register('email')}
      />

      {/* Phone - Required */}
      <FormInput
        label="Phone"
        type="tel"
        required
        placeholder="+12345678901"
        error={errors.phone?.message}
        {...register('phone')}
      />

      {/* Country - Required */}
      <FormInput
        label="Country"
        required
        error={errors.country?.message}
        {...register('country')}
      />

      {/* City */}
      <FormInput
        label="City"
        error={errors.city?.message}
        {...register('city')}
      />

      {/* Street */}
      <FormInput
        label="Street Address"
        error={errors.street?.message}
        {...register('street')}
      />

      {/* Account Segment - Feature 019 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Account Segment
        </label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          {...register('clientSegment')}
        >
          <option value="">Select segment...</option>
          <option value="BeautyProClinics">Beauty pro / Clinics</option>
          <option value="DropshipperReseller">Dropshipper / Reseller</option>
          <option value="Corporate">Corporate</option>
          <option value="BrandOwner">Brand owner</option>
          <option value="InfluencerCreator">Influencer / Creator</option>
          <option value="Other">Other</option>
        </select>
        {errors.clientSegment && (
          <p className="mt-1 text-sm text-red-600">{errors.clientSegment.message}</p>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? 'Creating...' : 'Create Account'}
        </button>
      </div>
    </form>
  );
}
